// useIframeHeight.ts
// Drop-in hook — import this in EmbeddedView.tsx and call it with the deps
// that change when content height changes. Zero changes needed to the embed script.

import { useCallback, useEffect, useRef, type RefObject } from "react";
import { isEmbeddableCollectionView } from "../embed/openCollectionNavigation";

/** Dispatched after in-embed client navigation so height is re-measured once the route settles. */
export const EMBED_HEIGHT_RECALC_EVENT = "searchbubble-embed-height-recalc";

export function requestEmbedIframeHeightRecalc() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(EMBED_HEIGHT_RECALC_EVENT));
  }
}

/** Recalc when running inside a parent iframe for embed / creator-pages views. */
export function requestEmbedIframeHeightRecalcFor(from: unknown) {
  if (
    typeof window !== "undefined" &&
    window.parent !== window &&
    isEmbeddableCollectionView(from)
  ) {
    requestEmbedIframeHeightRecalc();
  }
}

interface UseIframeHeightOptions {
  /** Minimum height to ever report. Default 200 (parent embed script uses its own fallback). */
  minHeight?: number;
  /** Extra deps that should trigger a height re-send (data loads, search toggle, etc.) */
  deps?: React.DependencyList;
}

function isPublicEmbedPath(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  return path === "/embed" || path.startsWith("/embed/");
}

export function useIframeHeight(
  containerRef: React.RefObject<HTMLElement>,
  options: UseIframeHeightOptions = {}
) {
  const { minHeight = 200, deps = [] } = options;
  const lastSent = useRef<number>(0);
  /** Last height posted to parent — kept across forced recalcs for shrink detection. */
  const lastReportedHeight = useRef<number>(0);

  // ─── Core measurement ────────────────────────────────────────────────────
  // Public /embed iframe: container scrollHeight only, and allow shrink on
  // search. Creator-page iframes keep max(document) + no-shrink-on-search so
  // pagination loaders do not yank the host scrollbar.
  const measure = useCallback((): number => {
    const containerEl = containerRef.current;
    const containerH = containerEl?.scrollHeight ?? 0;
    const bodyScroll = document.body?.scrollHeight ?? 0;
    const bodyOffset = document.body?.offsetHeight ?? 0;
    const docScroll = document.documentElement?.scrollHeight ?? 0;
    const documentH = Math.max(bodyScroll, bodyOffset, docScroll);

    if (isPublicEmbedPath()) {
      return Math.max(minHeight, containerH || documentH);
    }

    if (containerEl && containerH > 0) {
      const previous = lastReportedHeight.current;
      const isShrinking = previous > 0 && containerH < previous - 32;
      const isSearchResults =
        typeof document !== "undefined" &&
        Boolean(document.querySelector(".feed-optimized-view-search"));
      if (isShrinking && isSearchResults) {
        return Math.max(minHeight, previous);
      }
      if (isShrinking) {
        return Math.max(minHeight, containerH);
      }
      return Math.max(minHeight, containerH, documentH);
    }

    return Math.max(minHeight, documentH);
  }, [containerRef, minHeight]);

  // ─── Send — only fires when height actually changed ──────────────────────
  const send = useCallback(() => {
    if (window.parent === window) return;
    const h = measure();
    if (h === lastSent.current) return;

    lastSent.current = h;
    lastReportedHeight.current = h;
    window.parent.postMessage({ type: "searchbubble-height", height: h }, "*");
  }, [measure]);

  /** Force the next send even if height matches (e.g. after route swap). */
  const sendForced = useCallback(() => {
    lastSent.current = 0;
    send();
  }, [send]);

  // ─── MutationObserver on document.body ───────────────────────────────────
  useEffect(() => {
    if (window.parent === window) return;
    const mo = new MutationObserver(send);
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: false,
    });
    return () => mo.disconnect();
  }, [send]);

  // Observe the embed container only. Observing html/body re-fires when the
  // parent resizes the iframe (100vh), which feeds the last height back in.
  useEffect(() => {
    if (window.parent === window) return;
    const ro = new ResizeObserver(send);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [send, containerRef]);

  // ─── Staggered retries on mount ──────────────────────────────────────────
  useEffect(() => {
    if (window.parent === window) return;
    const timers = [0, 200, 600, 1500, 3000].map((ms) =>
      setTimeout(send, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [send]);

  // ─── Re-fire on caller-supplied deps (route changes, data loads, etc.) ───
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (window.parent === window) return;
    lastSent.current = 0;
    const timers = [0, 50, 150, 300, 600, 1200, 2000].map((ms) =>
      setTimeout(send, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [...deps, send]);

  // ─── Explicit recalc requests (carousel / View All navigation) ───────────
  useEffect(() => {
    if (window.parent === window) return;
    const onRecalc = () => sendForced();
    window.addEventListener(EMBED_HEIGHT_RECALC_EVENT, onRecalc);
    return () => window.removeEventListener(EMBED_HEIGHT_RECALC_EVENT, onRecalc);
  }, [sendForced]);

  // ─── Window resize ───────────────────────────────────────────────────────
  useEffect(() => {
    if (window.parent === window) return;
    window.addEventListener("resize", send);
    return () => window.removeEventListener("resize", send);
  }, [send]);

  return { send: sendForced };
}

const DEFAULT_EMBED_HEIGHT_RECALC_DELAYS_MS = [
  0, 50, 300, 600, 1200, 2000, 3500, 4500, 5500,
];

/** Stagger iframe height re-measurement until async route/content updates settle. */
export function scheduleEmbedIframeHeightRecalc(
  recalc: (() => void) | undefined,
  from: unknown,
  delaysMs: number[] = DEFAULT_EMBED_HEIGHT_RECALC_DELAYS_MS
) {
  if (typeof recalc !== "function") return () => {};
  if (window.parent === window) return () => {};
  if (!isEmbeddableCollectionView(from)) return () => {};

  const timers = delaysMs.map((ms) =>
    setTimeout(() => {
      recalc();
      requestEmbedIframeHeightRecalcFor(from);
    }, ms)
  );

  return () => timers.forEach(clearTimeout);
}

/** Re-measure once client navigation finishes (e.g. Home from an individual component). */
export function useEmbedNavigationHeightRecalc(
  isNavigating: boolean,
  recalc: (() => void) | undefined,
  from: unknown
) {
  const prevIsNavigating = useRef(isNavigating);

  useEffect(() => {
    const wasNavigating = prevIsNavigating.current;
    prevIsNavigating.current = isNavigating;

    if (!wasNavigating || isNavigating) return;
    return scheduleEmbedIframeHeightRecalc(recalc, from);
  }, [isNavigating, recalc, from]);
}

/** Re-measure iframe height after user interaction (clicks on titles, buttons, cards, etc.). */
export function useEmbedClickHeightRecalc(
  containerRef: RefObject<HTMLElement>,
  recalc: (() => void) | undefined,
  from: unknown
) {
  useEffect(() => {
    if (window.parent === window) return;
    if (!isEmbeddableCollectionView(from)) return;
    if (typeof recalc !== "function") return;

    let cancelScheduledRecalc = () => {};

    const onClick = (event: MouseEvent) => {
      const container = containerRef.current;
      if (container && !container.contains(event.target as Node)) return;
      cancelScheduledRecalc();
      cancelScheduledRecalc = scheduleEmbedIframeHeightRecalc(recalc, from);
    };

    document.addEventListener("click", onClick, true);

    return () => {
      document.removeEventListener("click", onClick, true);
      cancelScheduledRecalc();
    };
  }, [containerRef, recalc, from]);
}
